import { createContext, useContext, useEffect, useState } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { stripAttachedConnectorsBlock } from "../../../shared/connectors/attachedConnectors";
import { stripAttachedSkillsBlock } from "../../../shared/skills/attachedSkills";
import { stripAttachedStepsBlock } from "../../../shared/steps/attachedSteps";
import {
  readResearchSources,
  type ResearchSource,
} from "../../../shared/researchSources";
import { useToolOutput } from "../../hooks/useToolOutput";
import { researchCitationIds } from "./researchCitationLinks";
import { researchSourcePreview } from "./researchSourcePreview";
import { latestUserMessageBefore } from "./chatEntries";
import { AnswerSources, CitationPill } from "./ResearchSourceViews";

export type SourceResult = { sources: ResearchSource[]; loading: boolean };
export type ResearchSourceGroup = {
  messageId: string;
  title: string;
  sources: ResearchSource[];
};

export const ResearchSourcesPanelContext = createContext<{
  showSources: (group: ResearchSourceGroup) => void;
  onSourcesChange?: (groups: ResearchSourceGroup[]) => void;
} | null>(null);

const ResearchContext = createContext<{
  entries: readonly ChatEntryWithSubTables[];
  results: ReadonlyMap<string, SourceResult>;
}>({ entries: [], results: new Map() });

function SourceLoader({
  entry,
  setResults,
}: {
  entry: ChatEntryWithSubTables;
  setResults: React.Dispatch<React.SetStateAction<Map<string, SourceResult>>>;
}) {
  const { output, loading } = useToolOutput(entry.id, true);
  const storedOutput = output ?? entry.toolCall?.output;
  useEffect(() => {
    setResults((previous) => {
      const next = new Map(previous);
      next.set(entry.id, {
        sources: readResearchSources(storedOutput),
        loading,
      });
      return next;
    });
  }, [entry.id, storedOutput, loading, setResults]);
  return null;
}

export function ResearchCitations({
  entries,
  children,
  enabled = true,
}: {
  enabled?: boolean;
  entries: readonly ChatEntryWithSubTables[];
  children: React.ReactNode;
}) {
  const [results, setResults] = useState(new Map<string, SourceResult>());
  const workspace = useContext(ResearchSourcesPanelContext);
  const onSourcesChange = workspace?.onSourcesChange;
  useEffect(() => {
    if (!onSourcesChange) return;
    const groups: ResearchSourceGroup[] = [];
    if (enabled) {
      for (const entry of entries) {
        if (entry.entryType !== "AssistantMessage" || !entry.assistantMessage)
          continue;
        const group = resolveSourceGroup(
          entry.id,
          researchCitationIds(entry.assistantMessage.text),
          entries,
          results
        );
        if (group.sources.length) groups.push(group);
      }
    }
    onSourcesChange(groups);
  }, [entries, results, enabled, onSourcesChange]);
  return (
    <ResearchContext.Provider value={{ entries, results }}>
      {enabled &&
        entries
          .filter(isResearchEntry)
          .map((entry) => (
            <SourceLoader
              key={entry.id}
              entry={entry}
              setResults={setResults}
            />
          ))}
      {children}
    </ResearchContext.Provider>
  );
}

function isResearchEntry(entry: ChatEntryWithSubTables) {
  return (
    entry.entryType === "AssistantToolUse" &&
    (entry.toolCall?.toolName === "webSearch" ||
      entry.toolCall?.toolName === "webFetch") &&
    entry.toolCall.completedAt != null &&
    !entry.toolCall.isError
  );
}

export function resolveResearchCitation(
  sourceId: string,
  messageId: string | undefined,
  entries: readonly ChatEntryWithSubTables[],
  results: ReadonlyMap<string, SourceResult>
): { source?: ResearchSource; loading: boolean } {
  const messageIndex = entries.findIndex((entry) => entry.id === messageId);
  let loading = false;
  for (const entry of entries.slice(0, Math.max(0, messageIndex))) {
    if (!isResearchEntry(entry)) continue;
    const result = results.get(entry.id);
    loading ||= !result || result.loading;
    const source = result?.sources.find(
      (candidate) => candidate.id === sourceId
    );
    if (source) return { source, loading: false };
  }
  return { loading };
}

function resolveSourceGroup(
  messageId: string,
  sourceIds: string[],
  entries: readonly ChatEntryWithSubTables[],
  results: ReadonlyMap<string, SourceResult>
): ResearchSourceGroup {
  const sources = new Map<string, ResearchSource>();
  for (const id of sourceIds) {
    const { source } = resolveResearchCitation(id, messageId, entries, results);
    if (source) sources.set(source.url, source);
  }
  const prompt =
    latestUserMessageBefore(entries, messageId)?.userMessage?.text ?? "";
  const title = researchSourcePreview(
    stripAttachedConnectorsBlock(
      stripAttachedStepsBlock(
        stripAttachedSkillsBlock(
          prompt.replace(
            /<(code|element)-context\b[^>]*>[\s\S]*?<\/\1-context>/g,
            ""
          )
        )
      )
    )
  );
  return {
    messageId,
    title: title || "Response",
    sources: [...sources.values()],
  };
}

export function ResearchCitation({
  sourceIds,
  messageId,
}: {
  sourceIds: string[];
  messageId?: string;
}) {
  const { entries, results } = useContext(ResearchContext);
  const references = sourceIds.map((id) => ({
    id,
    ...resolveResearchCitation(id, messageId, entries, results),
  }));
  return <CitationPill references={references} />;
}

export function ResearchSources({
  messageId,
  sourceIds,
}: {
  messageId: string;
  sourceIds: string[];
}) {
  const { entries, results } = useContext(ResearchContext);
  const workspace = useContext(ResearchSourcesPanelContext);
  if (!workspace) return null;
  const group = resolveSourceGroup(messageId, sourceIds, entries, results);
  if (!group.sources.length) return null;
  return (
    <AnswerSources
      sources={group.sources}
      onShowSources={() => workspace.showSources(group)}
    />
  );
}
